/**
 * The knife's reach (02 §7): the man a stab would take right now, or null. One function for the stab and
 * for the HUD's in-reach cue, so what the screen promises and what the blade does cannot drift apart
 * (ROUND7 §1). Pure, and cheap enough to ask every frame: a handful of men are ever within reach, and the
 * statics are only walked for the one who would win.
 *
 * ROUND7: it was 1.35 m and 40 deg either side of the aim, and took the first man in the list. Measured in
 * play, the cursor on a man's chest lands a metre or more beyond his feet on the ground, so from behind a
 * sentry the aim often pointed past him or back at the ranger's own boots, and the stab went into air.
 * Now: a short lunge (STEALTH.knifeReach), a wider arc, a man at the elbow taken whatever the aim says, the
 * man nearest the aim first, and never through a wall.
 */

import { isAlive } from '../ctx';
import { isAutomated } from '../enemies/specials/kinds';
import { angleDiff, atan2, PI } from '../math';
import { segmentHit, type Statics } from '../statics';
import { STEALTH } from '../tuningStealth';
import type { Enemy, World } from '../types';

const DEG = PI / 180;

/**
 * Did he know the ranger was there? Fighting, searching, or his meter at 0.75: a knife kill on him is loud - he
 * shouts (02 §6.3). One rule for the stab and for the training coach that grades it (ROUND7).
 */
export function isAware(e: Enemy): boolean {
  return e.state === 'combat' || e.state === 'search' || e.detection >= 0.75;
}

/**
 * Nothing solid stands between the two points at knee height: a wall, a building, a sandbag run, a crate,
 * the wire. A walk-in building (a hangar floor) stops nobody's feet, so it stops no blade either.
 */
export function bladeClear(statics: Statics, ax: number, az: number, bx: number, bz: number): boolean {
  for (const c of statics.colliders) {
    if (!c.blocksMove || c.h <= STEALTH.kneeHeight) continue;
    if (segmentHit(c, ax, az, bx, bz)) return false;
  }
  return true;
}

export function knifeTarget(w: World, statics: Statics, aim: number): Enemy | null {
  const r = w.ranger;
  const reach = STEALTH.knifeReach + w.mods.knifeReachBonus;
  const pointBlank = STEALTH.knifePointBlank;
  const arc = STEALTH.knifeArcDeg * DEG;
  let best: Enemy | null = null;
  let bestOff = Infinity;
  let bestD2 = Infinity;
  for (const e of w.enemies) {
    // A knife is useless on armour (02 §7): it is not a hit, and certainly not a knife kill.
    if (!isAlive(e) || isAutomated(e.kind)) continue;
    const dx = e.x - r.x;
    const dz = e.z - r.z;
    const d2 = dx * dx + dz * dz;
    if (d2 > reach * reach) continue;
    const off = Math.abs(angleDiff(aim, atan2(dx, dz)));
    // At his elbow any man is in reach; further off he must be roughly where the ranger is aiming.
    if (off > arc && d2 > pointBlank * pointBlank) continue;
    // The man nearest the aim, then the nearer of two. Only a man who would win is worth the wall check.
    if (off > bestOff || (off === bestOff && d2 >= bestD2)) continue;
    if (!bladeClear(statics, r.x, r.z, e.x, e.z)) continue;
    best = e;
    bestOff = off;
    bestD2 = d2;
  }
  return best;
}
